import { debugError } from "./utils";
import { translateError } from "./errorTranslations";

// Client-side entry points to the checkout error catalog (CS-2942).
// Toast copy lives here because cancellations are client-detected events;
// card/full-page surfaces are server-rendered via /:code/error-card.

// copyForCode renders the wire code's localised copy, falling back to the
// English wording checkout has always shown for it (COI-779).
function copyForCode(code, english) {
  return translateError({ error: { code, message: english } }, english);
}

// Resolved lazily: the dictionary lives in the document, which does not exist
// when this module is first evaluated.
export const errorToastMessages = {
  get passkeyCancelled() {
    return copyForCode("AUTH_PASSKEY_CANCELLED", "Passkey cancelled");
  },
  get walletConnectCancelled() {
    return copyForCode("PAYMENT_WALLET_CONNECT_CANCELLED", "Wallet connection cancelled");
  },
  get paymentDeclinedInWallet() {
    return copyForCode("PAYMENT_DECLINED_IN_WALLET", "Payment cancelled. No funds were taken.");
  },
};

export function showErrorToast(message) {
  document.dispatchEvent(new CustomEvent("toast:show", { detail: { message } }));
}

// showErrorToastFor is showErrorToast for an error that came back from the API:
// it renders the localised copy for error.code, the server's message when the
// code is one checkout does not localise, and fallback when there is neither.
export function showErrorToastFor(error, fallback) {
  const message = translateError(error, fallback);
  if (!message) return false;
  showErrorToast(message);
  return true;
}

// Fetches the server-rendered catalog surface for errorKey and hands it to
// Turbo. code/session default to the checkout URL convention (/:code?session=).
export async function showErrorCard(errorKey, { code, session } = {}) {
  const url = new URL(window.location.href);
  const resolvedCode = code || url.pathname.split("/").filter(Boolean)[0] || "";
  const resolvedSession = session || url.searchParams.get("session") || "";
  if (!errorKey || !resolvedCode || !resolvedSession) {
    return false;
  }

  try {
    const response = await fetch(
      `/${resolvedCode}/error-card?key=${encodeURIComponent(errorKey)}&session=${encodeURIComponent(resolvedSession)}`,
      { headers: { Accept: "text/vnd.turbo-stream.html" } }
    );
    if (!response.ok) {
      return false;
    }
    Turbo.renderStreamMessage(await response.text());
    return true;
  } catch (error) {
    debugError("showErrorCard: fetch failed", error);
    return false;
  }
}

// True for WebAuthn prompt dismissals/timeouts and EIP-1193 user rejections
// (code 4001) — deliberate customer actions that get a toast, not a card.
export function isUserCancellation(error) {
  if (!error) return false;
  if (error.name === "NotAllowedError" || error.name === "AbortError") return true;
  if (error.code === 4001 || error.cause?.code === 4001) return true;
  const message = String(error.message || "").toLowerCase();
  return message.includes("user rejected") || message.includes("user denied");
}
